<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex items-center justify-between px-3 py-2 border-b border-slate-600 flex-shrink-0">
      <span class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Theme Library</span>
      <div class="flex items-center gap-1">
        <button class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors" title="Create theme" @click="createTheme">+ New</button>
        <button class="text-slate-400 hover:text-cyan-300 text-xs px-2 py-1 rounded hover:bg-slate-600 transition-colors" title="Import JSONL theme" @click="$refs.importInput.click()">Import</button>
        <button class="text-slate-400 hover:text-cyan-300 text-xs px-2 py-1 rounded hover:bg-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title="Use selected theme in the design" :disabled="!selectedTheme" @click="useTheme(selectedTheme)">Use</button>
        <button class="text-slate-400 hover:text-cyan-300 text-xs px-2 py-1 rounded hover:bg-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title="Export selected theme" :disabled="!selectedTheme" @click="exportTheme">Export</button>
        <button class="text-slate-400 hover:text-cyan-300 text-xs px-2 py-1 rounded hover:bg-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title="Upload selected theme to device" :disabled="!selectedTheme || !canUploadToDevice || uploadingThemeId !== null" @click="uploadTheme(selectedTheme)">Device</button>
        <input ref="importInput" class="hidden" type="file" accept=".jsonl,application/jsonl,text/plain" @change="importTheme" />
      </div>
    </div>

    <ResourceListToolbar v-if="themes.length > 0" :search="search" @update:search="search = $event" :sortKey="sortKey" @update:sortKey="sortKey = $event" :sortOptions="sortOptions" />

    <div v-if="loading" class="px-3 py-6 text-xs text-slate-500 text-center">Loading themes…</div>
    <div v-else-if="error" class="mx-2 mt-2 rounded border border-red-700/60 bg-red-950/30 p-2 text-xs text-red-200">{{ error }}</div>
    <template v-else>
      <div v-if="deviceProgress" class="mx-2 mt-2 rounded border p-2 text-xs" :class="deviceProgress.kind === 'error' ? 'border-red-700/60 bg-red-950/30 text-red-200' : deviceProgress.kind === 'done' ? 'border-emerald-700/60 bg-emerald-950/30 text-emerald-200' : 'border-cyan-700/60 bg-cyan-950/30 text-cyan-200'">
        <div class="flex items-center justify-between gap-2">
          <span class="flex items-center gap-2">
            <span v-if="deviceProgress.kind === 'running'" class="inline-block w-3 h-3 rounded-full border-2 border-cyan-300 border-t-transparent animate-spin" aria-hidden="true"></span>
            <span>{{ deviceProgress.message }}</span>
          </span>
          <span v-if="deviceProgress.kind !== 'error'" class="tabular-nums">{{ deviceProgress.percent }}%</span>
        </div>
        <div class="mt-1 h-1.5 rounded-full bg-slate-700 overflow-hidden" role="progressbar" :aria-valuenow="deviceProgress.percent" aria-valuemin="0" aria-valuemax="100">
          <div class="h-full rounded-full transition-all duration-300" :class="deviceProgress.kind === 'error' ? 'bg-red-400' : deviceProgress.kind === 'done' ? 'bg-emerald-400' : 'bg-cyan-400'" :style="{ width: `${deviceProgress.percent}%` }"></div>
        </div>
      </div>
      <div v-if="deviceStatus" class="mx-2 mt-2 rounded border p-2 text-xs" :class="deviceStatusTone === 'error' ? 'border-red-700/60 bg-red-950/30 text-red-200' : deviceStatusTone === 'success' ? 'border-emerald-700/60 bg-emerald-950/30 text-emerald-200' : 'border-cyan-700/60 bg-cyan-950/30 text-cyan-200'">{{ deviceStatus }}</div>
      <div class="flex-1 overflow-y-auto">
        <div v-if="themes.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">No themes yet.<br>Click <strong class="text-slate-400">+ New</strong> to create one.</div>
        <div v-else-if="filteredThemes.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">No themes match your search.</div>

        <ResourceListItem v-for="theme in filteredThemes" :key="theme.id" :idLabel="theme.runtimeId == null ? '' : '#' + theme.runtimeId" :title="theme.name" :meta="rowMeta(theme)" @select="openTheme(theme)">
        <template #actions>
          <button class="text-slate-400 hover:text-cyan-300 p-1 rounded hover:bg-slate-600 transition-colors" title="Export" @click="exportTheme(theme)">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" /></svg>
          </button>
          <button class="text-slate-400 hover:text-cyan-300 p-1 rounded hover:bg-slate-600 transition-colors" title="Use in design" @click.stop="useTheme(theme)">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14m-6-6l6 6-6 6" /></svg>
          </button>
          <button class="text-slate-400 hover:text-cyan-300 p-1 rounded hover:bg-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title="Upload to device" :disabled="!canUploadToDevice || uploadingThemeId !== null" @click.stop="uploadTheme(theme)">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 21V9m0 0l-4 4m4-4l4 4M5 3h14" /></svg>
          </button>
          <button class="text-slate-400 hover:text-red-400 p-1 rounded hover:bg-slate-600 transition-colors" title="Delete" @click="pendingDeleteId = theme.id">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v3m1-7V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
          </button>
        </template>
        </ResourceListItem>
      </div>
    </template>
  </div>

  <ConfirmationModal :show="pendingDeleteId !== null" title="Delete Theme" :message="pendingDeleteTheme ? `Are you sure you want to delete <strong>${pendingDeleteTheme.name}</strong>?` : 'Are you sure?'" @confirm="confirmDelete" @cancel="pendingDeleteId = null" />

  <FloatingPopup id="theme-editor" :modelValue="editingTheme !== null" @update:modelValue="closeEditor" :title="editingTheme?.name || 'Theme'" :width="1180" :height="760" :minwidth="720" :minheight="500" :position="{ x: 300, y: 80 }" contentClass="p-0 overflow-hidden">
    <ThemeEditor v-if="editingTheme" ref="themeEditor" :initialText="editingTheme.text" :initialFileName="editingTheme.fileName" @save="saveEditedTheme" />
  </FloatingPopup>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import defaultThemeText from '../../docs/themes/material-dark.jsonl?raw'
import ResourceListToolbar from '../components/resources/ResourceListToolbar.vue'
import ResourceListItem from '../components/resources/ResourceListItem.vue'
import FloatingPopup from '../components/FloatingPopup.vue'
import ConfirmationModal from '../components/ConfirmationModal.vue'
import ThemeEditor from './ThemeEditor.vue'
import { createNewTheme, parseThemeJsonl, serializeThemeJsonl } from './themeModel.js'
import { deleteTheme, listThemes, saveTheme } from './themeDB.js'
import { uploadThemeToDevice } from './themeTransfer.js'
import { loadExternalThemes, externalThemeKey } from './themeRegistry.js'
import mqttService from '../services/MqttService'

const props = defineProps({
  settings: { type: Object, default: () => ({}) },
})
const emit = defineEmits(['use-theme'])

const themes = ref([])
const selectedThemeId = ref(null)
const editingTheme = ref(null)
const pendingDeleteId = ref(null)
const loading = ref(true)
const error = ref('')
const search = ref('')
const sortKey = ref('name-asc')
const themeEditor = ref(null)
const uploadingThemeId = ref(null)
const deviceStatus = ref('')
const deviceStatusTone = ref('info')
const deviceProgress = ref(null)

const sortOptions = [
  { value: 'name-asc', label: 'Name ↑' },
  { value: 'name-desc', label: 'Name ↓' },
  { value: 'id-asc', label: 'ID ↑' },
  { value: 'id-desc', label: 'ID ↓' },
]

const selectedTheme = computed(() => themes.value.find(theme => theme.id === selectedThemeId.value) ?? null)
const canUploadToDevice = computed(() => Boolean(props.settings?.tauri && (props.settings?.plate?.name || props.settings?.plate?.host)))
const pendingDeleteTheme = computed(() => themes.value.find(theme => theme.id === pendingDeleteId.value) ?? null)
const filteredThemes = computed(() => {
  const query = search.value.trim().toLowerCase()
  let list = themes.value.filter(theme => !query || theme.name.toLowerCase().includes(query) || String(theme.runtimeId ?? '').includes(query))
  list = [...list]
  switch (sortKey.value) {
    case 'name-desc': list.sort((a, b) => b.name.localeCompare(a.name)); break
    case 'id-asc': list.sort((a, b) => Number(a.runtimeId ?? 0) - Number(b.runtimeId ?? 0)); break
    case 'id-desc': list.sort((a, b) => Number(b.runtimeId ?? 0) - Number(a.runtimeId ?? 0)); break
    default: list.sort((a, b) => a.name.localeCompare(b.name)); break
  }
  return list
})

function rowMeta(theme) {
  return [`LVGL ${theme.lvgl ?? 7}`, theme.default ? 'Default theme' : 'External theme', theme.fileName]
}

async function refresh() {
  loading.value = true
  error.value = ''
  try {
    themes.value = await listThemes()
    if (themes.value.length === 0) {
      const document = parseThemeJsonl(defaultThemeText)
      const metadata = document.records.find(record => record.type === 'm').data
      await saveTheme({ name: metadata.n, runtimeId: metadata.id, lvgl: metadata.lv, default: Boolean(metadata.df), fileName: 'material-dark.jsonl', text: defaultThemeText, source: 'bundled' })
      themes.value = await listThemes()
    }
    await loadExternalThemes()
    if (!selectedThemeId.value || !themes.value.some(theme => theme.id === selectedThemeId.value)) selectedThemeId.value = themes.value[0]?.id ?? null
  } catch (err) {
    error.value = err.message || 'Could not load themes.'
  } finally {
    loading.value = false
  }
}

function useTheme(theme) {
  if (theme) emit('use-theme', { ...theme, selection: externalThemeKey(theme.id) })
}

function createTheme() {
  const document = createNewTheme(`new-theme-${themes.value.length + 1}`)
  const metadata = document.records.find(record => record.type === 'm').data
  saveTheme({ name: metadata.n, runtimeId: metadata.id, lvgl: metadata.lv, default: false, fileName: `${metadata.n}.jsonl`, text: serializeThemeJsonl(document), source: 'template' }).then(async row => {
    await refresh()
    selectedThemeId.value = row.id
    openTheme(row)
  }).catch(err => { error.value = err.message || 'Could not create theme.' })
}

async function importTheme(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  try {
    const text = await file.text()
    const document = parseThemeJsonl(text)
    const metadata = document.records.find(record => record.type === 'm').data
    const row = await saveTheme({ name: metadata.n, runtimeId: metadata.id, lvgl: metadata.lv, default: Boolean(metadata.df), fileName: file.name, text, source: 'imported' })
    await refresh()
    selectedThemeId.value = row.id
  } catch (err) {
    error.value = `Could not import theme: ${err.message}`
  }
}

function openTheme(theme) {
  selectedThemeId.value = theme.id
  editingTheme.value = { ...theme }
}

function exportTheme(theme = selectedTheme.value) {
  if (!theme) return
  try {
    // Validate the source bytes, but do not serialize them again. JSONL
    // spacing and blank lines are part of the imported file's round-trip.
    parseThemeJsonl(theme.text, { normalizeAliases: false })
  } catch (err) {
    error.value = `Could not export theme: ${err.message}`
    return
  }
  const blob = new Blob([theme.text], { type: 'application/jsonl' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = theme.fileName || `${theme.name}.jsonl`
  link.click()
  URL.revokeObjectURL(url)
}

async function uploadTheme(theme) {
  if (!theme || !canUploadToDevice.value) return
  uploadingThemeId.value = theme.id
  deviceStatus.value = ''
  deviceStatusTone.value = 'info'
  deviceProgress.value = { phase: 'starting', message: 'Preparing theme upload…', percent: 0, kind: 'running' }
  error.value = ''

  const setPhase = ({ phase, message, percent }) => {
    deviceProgress.value = { phase, message, percent, kind: 'running' }
  }

  let result
  try {
    result = await uploadThemeToDevice(theme, props.settings.plate, { onPhase: setPhase })
    deviceStatus.value = `Uploaded and verified ${result.path} (${result.bytes} bytes). Activating on the device…`
  } catch (err) {
    deviceProgress.value = { phase: 'error', message: 'Theme upload failed.', percent: deviceProgress.value?.percent ?? 0, kind: 'error' }
    deviceStatusTone.value = 'error'
    deviceStatus.value = `Theme upload failed: ${err.message}`
    uploadingThemeId.value = null
    return
  }

  if (theme.runtimeId == null) {
    deviceProgress.value = { phase: 'verified', message: 'Theme file verified, but no firmware theme ID is available for activation.', percent: 72, kind: 'error' }
    deviceStatusTone.value = 'error'
    deviceStatus.value = 'Upload verified, but activation was not attempted because the theme has no firmware ID.'
    uploadingThemeId.value = null
    return
  }

  let activationWait
  try {
    setPhase({ phase: 'activate', message: `Activating theme ${theme.runtimeId} on the device…`, percent: 82 })
    if (!mqttService.isConnected()) await mqttService.connectToBroker(props.settings)
    // Register before publishing: the firmware answers immediately with the
    // current theme status on hasp/<plate>/state/theme.
    activationWait = createThemeActivationWait(theme.runtimeId)
    await mqttService.sendMqttCommand('/command/theme', String(theme.runtimeId))
    setPhase({ phase: 'confirm', message: 'Waiting for activation confirmation from the device…', percent: 94 })
    const status = await activationWait.promise
    const activeName = status?.active?.name ? ` (${status.active.name})` : ''
    deviceProgress.value = { phase: 'done', message: `Theme ${theme.runtimeId}${activeName} is active on the device.`, percent: 100, kind: 'done' }
    deviceStatusTone.value = 'success'
    deviceStatus.value = `Theme ${theme.runtimeId}${activeName} is active on the device. File verified at ${result.path}.`
  } catch (err) {
    activationWait?.cancel()
    deviceProgress.value = { phase: 'error', message: 'Theme file uploaded, but activation was not confirmed.', percent: 94, kind: 'error' }
    deviceStatusTone.value = 'error'
    deviceStatus.value = `Upload verified, activation failed: ${err.message}`
  } finally {
    uploadingThemeId.value = null
  }
}

function createThemeActivationWait(runtimeId, timeoutMs = 7000) {
  const listenerName = `theme-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`
  let timer
  let settled = false
  let resolvePromise
  let rejectPromise

  const cleanup = () => {
    clearTimeout(timer)
    mqttService.unregisterListener(listenerName)
  }

  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve
    rejectPromise = reject
    timer = setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      reject(new Error('No activation confirmation was received from the device'))
    }, timeoutMs)
  })

  mqttService.registerListener(listenerName, (_timestamp, topic, payload) => {
    if (settled || !String(topic || '').endsWith('/state/theme')) return
    let status
    try {
      status = typeof payload === 'string' ? JSON.parse(payload) : payload
    } catch {
      return
    }
    if (Number(status?.active?.id) !== Number(runtimeId)) return
    settled = true
    cleanup()
    resolvePromise(status)
  })

  return {
    promise,
    cancel() {
      if (settled) return
      settled = true
      cleanup()
      rejectPromise(new Error('Activation confirmation cancelled'))
    },
  }
}

async function confirmDelete() {
  const id = pendingDeleteId.value
  pendingDeleteId.value = null
  if (!id) return
  if (editingTheme.value?.id === id) editingTheme.value = null
  await deleteTheme(id)
  await refresh()
}

function closeEditor(value = false) {
  if (value !== false) return
  if (themeEditor.value?.dirty && !window.confirm('Discard unsaved theme changes?')) return
  editingTheme.value = null
}

async function saveEditedTheme({ text, fileName }) {
  if (!editingTheme.value) return
  const editedThemeId = editingTheme.value.id
  const wasActive = props.settings?.plate?.hasp_theme === externalThemeKey(editedThemeId)
  try {
    const document = parseThemeJsonl(text)
    const metadata = document.records.find(record => record.type === 'm').data
    const normalizedText = serializeThemeJsonl(document)
    const row = await saveTheme({ ...editingTheme.value, name: metadata.n, runtimeId: metadata.id, lvgl: metadata.lv, default: Boolean(metadata.df), fileName: fileName || editingTheme.value.fileName, text: normalizedText })
    await refresh()
    selectedThemeId.value = row.id
    editingTheme.value = null
    if (wasActive) useTheme(row)
  } catch (err) {
    error.value = `Could not save theme: ${err.message}`
  }
}

onMounted(refresh)
</script>
